import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Trash2, CheckCircle2, Ticket, CalendarHeart } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { myEvents } from '@/lib/myEvents';
import { buildEventIndex, resolveSavedEvent } from '@/lib/eventAdapter';
import { useAuth } from '@/contexts/AuthContext';
import { usePageMeta } from '@/hooks/usePageMeta';

export const MyEvents = () => {
  usePageMeta('My Events', 'Events you’ve saved and the ones you’re attending on SoundWolves.');
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [eventsById, setEventsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, catalog] = await Promise.all([
        myEvents.list(),
        api.get('/events').catch(() => []),
      ]);
      // Index catalog events under all identifier fields (id / event_id / _id /
      // slug) so saved records resolve regardless of which one they stored.
      setEventsById(buildEventIndex(catalog || []));
      setItems(mine || []);
    } catch (e) {
      toast.error(e.message || 'Could not load your events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    load();
  }, [authLoading, isAuthenticated, load]);

  const handleConfirm = async (eventId) => {
    setBusy(eventId);
    try {
      await myEvents.confirm(eventId);
      toast.success("You're marked as attending!");
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not confirm');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (eventId) => {
    setBusy(eventId);
    try {
      await myEvents.remove(eventId);
      toast.info('Removed from My Events');
      setItems((prev) => prev.filter((i) => i.event_id !== eventId));
    } catch (e) {
      toast.error(e.message || 'Could not remove');
    } finally {
      setBusy(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 pb-24">
        <div className="container mx-auto px-4 text-center py-20">
          <CalendarHeart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-3xl font-display font-bold mb-2">My Events</h1>
          <p className="text-muted-foreground mb-6">Log in to save events and track what you’re attending.</p>
          <Button variant="premium" size="lg" onClick={() => navigate('/login')}>Log In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-3">
            My <span className="text-gradient-gold">Events</span>
          </h1>
          <p className="text-muted-foreground text-lg">Events you’ve saved and the ones you’re attending</p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <CalendarHeart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-display font-bold mb-2">No saved events yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Browse events and tap “Save to My Events” to keep track of them here.
            </p>
            <Link to="/events">
              <Button variant="premium" size="lg"><Ticket className="w-5 h-5 mr-2" />Browse Events</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-events-list">
            {items.map((item) => {
              const ev = resolveSavedEvent(eventsById, item);
              const attending = item.status === 'attending';
              return (
                <Card key={item.id} className="border-border/50 hover-lift">
                  <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
                    {ev?.image && (
                      <img src={ev.image} alt={ev.title} className="w-full sm:w-32 h-32 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-xl font-display font-bold truncate">
                          {ev?.title || `Event ${item.event_id}`}
                        </h3>
                        <Badge variant={attending ? 'default' : 'secondary'}>
                          {attending ? 'Attending' : 'Saved'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        {ev?.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{ev.date}</span>}
                        {ev?.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{ev.venue}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      {ev && (
                        <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => navigate(`/event/${ev.slug || item.event_id}`)}>
                          View
                        </Button>
                      )}
                      {!attending && (
                        <Button
                          variant="premium"
                          className="flex-1 sm:flex-none"
                          disabled={busy === item.event_id}
                          onClick={() => handleConfirm(item.event_id)}
                          data-testid={`confirm-${item.event_id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />I'm attending
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="hover:text-destructive"
                        disabled={busy === item.event_id}
                        onClick={() => handleRemove(item.event_id)}
                        data-testid={`remove-${item.event_id}`}
                        aria-label="Remove from My Events"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEvents;
